import React, { useCallback, useEffect, useState } from "react";
import { Switch, FormControlLabel, CircularProgress, Divider } from "@mui/material";
import Icon from "@mdi/react";
import {
	mdiBell,
	mdiCheckCircle,
	mdiAlertCircle,
	mdiKeyVariant,
	mdiTimerSandComplete,
	mdiCreditCardOutline,
} from "@mdi/js";
import rootStyles from "../styles.module.scss";
import styles from "./styles.module.scss";
import {
	NotificationServices,
	NotificationType,
	NotistackServices,
	PerTypeOverrides,
	PublicNotificationPreferencesDTO,
} from "shared/services";

interface TypeRow {
	type: NotificationType;
	label: string;
	description: string;
	icon: string;
}

const TYPE_ROWS: TypeRow[] = [
	{
		type: "task_completed",
		label: "Tarefa concluida",
		description: "Quando uma tarefa termina com sucesso",
		icon: mdiCheckCircle,
	},
	{
		type: "task_failed",
		label: "Tarefa com erro",
		description: "Quando uma tarefa falha ou e cancelada",
		icon: mdiAlertCircle,
	},
	{
		type: "oauth_required",
		label: "OAuth necessario",
		description: "Quando uma integracao precisa de re-autenticacao",
		icon: mdiKeyVariant,
	},
	{
		type: "rate_limit_hit",
		label: "Limite de tokens",
		description: "Quando um membro/equipe atinge o teto de tokens",
		icon: mdiTimerSandComplete,
	},
	{
		type: "stripe_invoice_paid",
		label: "Pagamento confirmado",
		description: "Quando uma fatura Stripe e paga",
		icon: mdiCreditCardOutline,
	},
	{
		type: "stripe_payment_failed",
		label: "Pagamento falhou",
		description: "Quando uma cobranca Stripe e recusada",
		icon: mdiAlertCircle,
	},
];

const isChannelEnabled = (
	type: NotificationType,
	channel: "in_app" | "email",
	globalEnabled: boolean,
	overrides: PerTypeOverrides,
): boolean => {
	const override = overrides[type]?.[channel];
	return typeof override === "boolean" ? override : globalEnabled;
};

export const NotificationsPage: React.FC = () => {
	const [prefs, setPrefs] = useState<PublicNotificationPreferencesDTO | null>(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const p = await NotificationServices.getPreferences();
				if (!cancelled) setPrefs(p);
			} catch {
				if (!cancelled) NotistackServices.showErrorMessage("Erro ao carregar preferencias");
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	const persist = useCallback(async (next: Partial<PublicNotificationPreferencesDTO>) => {
		setSaving(true);
		try {
			const updated = await NotificationServices.updatePreferences({
				inAppEnabled: next.inAppEnabled,
				emailEnabled: next.emailEnabled,
				perTypeOverrides: next.perTypeOverrides,
			});
			setPrefs(updated);
		} catch {
			NotistackServices.showErrorMessage("Erro ao salvar preferencias");
		} finally {
			setSaving(false);
		}
	}, []);

	const handleGlobalToggle = (channel: "in_app" | "email", value: boolean) => {
		if (!prefs) return;
		const next = { ...prefs, [channel === "in_app" ? "inAppEnabled" : "emailEnabled"]: value };
		setPrefs(next);
		void persist(next);
	};

	const handlePerTypeToggle = (type: NotificationType, channel: "in_app" | "email", value: boolean) => {
		if (!prefs) return;
		const nextOverrides: PerTypeOverrides = {
			...prefs.perTypeOverrides,
			[type]: { ...prefs.perTypeOverrides[type], [channel]: value },
		};
		const next = { ...prefs, perTypeOverrides: nextOverrides };
		setPrefs(next);
		void persist(next);
	};

	if (loading || !prefs) {
		return (
			<div className={rootStyles.contentWrapper}>
				<div className={styles.loading}>
					<CircularProgress size={24} color="inherit" />
				</div>
			</div>
		);
	}

	return (
		<div className={rootStyles.contentWrapper}>
			<div className={rootStyles.section}>
				<div className={rootStyles.sectionHeader}>
					<Icon path={mdiBell} size={1.4} className={rootStyles.icon} />
					<div className={rootStyles.titles}>
						<h3>Notificacoes</h3>
						<p>Escolha por quais canais voce quer receber alertas e quais eventos disparam cada canal.</p>
					</div>
				</div>

				<div className={rootStyles.card}>
					<h4>Canais globais</h4>
					<p className={styles.help}>
						Define o padrao para todos os tipos. Voce pode sobrescrever por evento na lista abaixo.
					</p>
					<div className={styles.toggleRow}>
						<FormControlLabel
							control={
								<Switch
									checked={prefs.inAppEnabled}
									onChange={(_, v) => handleGlobalToggle("in_app", v)}
									disabled={saving}
								/>
							}
							label="Notificacoes no app (sino)"
						/>
						<FormControlLabel
							control={
								<Switch
									checked={prefs.emailEnabled}
									onChange={(_, v) => handleGlobalToggle("email", v)}
									disabled={saving}
								/>
							}
							label="Notificacoes por e-mail"
						/>
					</div>
				</div>

				<div className={rootStyles.card}>
					<h4>Por tipo de evento</h4>
					<p className={styles.help}>
						Sobrescreva o canal global para tipos especificos. Ex: desligar e-mail para "tarefa concluida"
						mantendo ligado para "tarefa com erro".
					</p>
					<div className={styles.typeList}>
						{TYPE_ROWS.map((row, idx) => {
							const inApp = isChannelEnabled(row.type, "in_app", prefs.inAppEnabled, prefs.perTypeOverrides);
							const email = isChannelEnabled(row.type, "email", prefs.emailEnabled, prefs.perTypeOverrides);
							return (
								<React.Fragment key={row.type}>
									{idx > 0 && <Divider />}
									<div className={styles.typeRow}>
										<div className={styles.typeInfo}>
											<Icon path={row.icon} size={0.9} />
											<div>
												<div className={styles.typeLabel}>{row.label}</div>
												<div className={styles.typeDesc}>{row.description}</div>
											</div>
										</div>
										<div className={styles.typeChannels}>
											<FormControlLabel
												control={
													<Switch
														size="small"
														checked={inApp}
														onChange={(_, v) => handlePerTypeToggle(row.type, "in_app", v)}
														disabled={saving}
													/>
												}
												label="App"
												labelPlacement="bottom"
											/>
											<FormControlLabel
												control={
													<Switch
														size="small"
														checked={email}
														onChange={(_, v) => handlePerTypeToggle(row.type, "email", v)}
														disabled={saving}
													/>
												}
												label="E-mail"
												labelPlacement="bottom"
											/>
										</div>
									</div>
								</React.Fragment>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);
};
